import { useMemo, useRef, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import { GtpChart } from '@/components/GtpBreakdown/GtpChart';
import { getGtpItemsFromRow } from '@/utils/formatGtpList';
import { getSourceDisplayName } from '@/modules/search/searchGrid/helpers/sourceTabMetadata';
import { buildAnalyticsSearchTitle } from '../GdaAnalytics/analyticsHelpers';
import { downloadAnalyticsPng } from '../GdaAnalytics/analyticsDownload';
import { ChartPanel } from '../GdaAnalytics/analyticsUi';
import Loader from '../../Common/Loader';
import { NoRecordsFound } from '../NoRecordsFound';
import { useGenesGtpHeatmapData } from './useGenesGtpHeatmapData';
import { trackEvent } from '@/utils/analytics';
import '../GdaAnalytics/GdaAnalytics.css';
import './GenesGtpHeatmap.css';

type GenesGtpSingleProfileProps = {
  source: string;
  view: string;
  names: Array<{ id: string; name?: string; label?: string }>;
};

const CHART_HEIGHT = 22 * 18 + 60;

function safeFileStem(title: string): string {
  return `gtap-profile-${String(title || 'search')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 60)}`;
}

export function GenesGtpSingleProfile({ source, view, names }: GenesGtpSingleProfileProps) {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [isDownloadingPng, setIsDownloadingPng] = useState(false);
  const { data, isLoading, isEmpty, isPanelActive } = useGenesGtpHeatmapData({
    source,
    view,
    names,
  });

  const items = useMemo(() => getGtpItemsFromRow(data[0]), [data]);
  const searchTitle = buildAnalyticsSearchTitle(names);
  const sourceLabel = getSourceDisplayName(source);

  const handleDownloadPng = async () => {
    if (isDownloadingPng) return;
    trackEvent({
      name: 'download_result',
      buttonId: 'genes_gtap_download_png',
      label: 'Download PNG',
      page: 'search_results',
      section: 'graphs',
      feature: 'gtap',
      eventCategory: 'graphs',
    });
    setIsDownloadingPng(true);
    try {
      await downloadAnalyticsPng(dashboardRef.current, `${safeFileStem(searchTitle)}.png`);
    } finally {
      setIsDownloadingPng(false);
    }
  };

  if (!isPanelActive || isLoading) {
    return (
      <div className="gda-analytics-loading">
        <Loader />
      </div>
    );
  }

  if (isEmpty || items.length === 0) {
    return <NoRecordsFound />;
  }

  return (
    <div ref={dashboardRef} className="gda-analytics-dashboard gtp-heatmap mr-2">
      <header className="gda-analytics-header">
        <div className="gda-analytics-header__title-row">
          <h2 className="gda-analytics-header__title">GTAP</h2>
          <SharedBadge
            text={`Source: ${sourceLabel}`}
            variant="primaryLight"
            size="xs"
            className="gda-analytics-header__source-badge"
          />
        </div>
        <div className="gda-analytics-header__actions">
          <SharedButton
            variant="secondaryOutline"
            size="xs"
            className="gda-analytics-header__download mr-0"
            disabled={isDownloadingPng}
            onClick={handleDownloadPng}
            iconLeft={<ArrowDownTrayIcon className="h-4 w-4" />}
          >
            {isDownloadingPng ? 'Downloading...' : 'Download PNG'}
          </SharedButton>
        </div>
      </header>

      <ChartPanel
        title="Gene Therapeutic Area Profile"
        className="gda-analytics-panel--wide gtp-heatmap-panel"
      >
        <div
          className="gtp-single-profile__chart"
          style={{ height: CHART_HEIGHT }}
          data-testid="gtp-single-profile-chart"
        >
          <GtpChart items={items} />
        </div>
      </ChartPanel>
    </div>
  );
}
